import { useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Loader2 } from "lucide-react";
import { AuthField } from "@/components/AuthField";
import { BuildEaseLogo } from "@/components/BuildEaseLogo";
import { CADGridBackground } from "@/components/CADGridBackground";
import { OnboardingProgress } from "@/components/OnboardingProgress";
import { TrustStrip } from "@/components/TrustStrip";
import { putSecret, SECRETS } from "@/lib/secureStore";
import { useOnboardingStore } from "@/store/onboardingStore";

const EASE = [0.22, 1, 0.36, 1] as const;
const ID_RE = /^[A-Za-z0-9]{6,20}$/;

const item = {
  hidden: { opacity: 0, y: 18 },
  show: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { delay: 0.12 + i * 0.08, duration: 0.6, ease: EASE },
  }),
};

const maskId = (v: string) => (v.length > 4 ? "•".repeat(v.length - 4) + v.slice(-4) : v);

export default function OnboardingVerificationScreen() {
  const navigate = useNavigate();
  const markVerified = useOnboardingStore((s) => s.markVerified);
  const [idNumber, setIdNumber] = useState("");
  const [idFocused, setIdFocused] = useState(false);
  const [address, setAddress] = useState("");
  const [errors, setErrors] = useState<{ idNumber?: string; address?: string }>({});
  const [submitting, setSubmitting] = useState(false);
  const [leaving, setLeaving] = useState(false);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const next: typeof errors = {};
    const id = idNumber.replace(/\s/g, "");
    if (!ID_RE.test(id)) next.idNumber = "6–20 letters or digits";
    if (address.trim().length < 8) next.address = "Enter your full address";
    setErrors(next);
    if (Object.keys(next).length) return;
    setSubmitting(true);
    await putSecret(SECRETS.identity, { idNumber: id, address: address.trim() });
    markVerified(id.slice(-4));
    setLeaving(true);
    window.setTimeout(() => navigate("/projects/new", { replace: true }), 320);
  };

  return (
    <div
      data-testid="onboarding-verification-screen"
      className="relative flex min-h-screen items-center justify-center overflow-hidden bg-obsidian px-6 py-12"
    >
      <CADGridBackground opacity={0.28} />
      <div
        aria-hidden
        className="absolute left-1/2 top-[40%] h-[560px] w-[560px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-safety/[0.05] blur-3xl"
      />

      <AnimatePresence>
        {!leaving && (
          <motion.form
            onSubmit={onSubmit}
            noValidate
            initial="hidden"
            animate="show"
            exit={{ opacity: 0, x: -24, transition: { duration: 0.3, ease: "easeIn" } }}
            className="relative flex w-full max-w-sm flex-col gap-7"
          >
            <motion.div variants={item} custom={0} className="flex flex-col gap-6">
              <BuildEaseLogo height={36} />
              <OnboardingProgress step={2} total={2} />
            </motion.div>

            <div className="flex flex-col gap-2">
              <div className="overflow-hidden">
                <motion.h1
                  data-testid="onboarding-verification-headline"
                  initial={{ y: "110%" }}
                  animate={{ y: 0 }}
                  transition={{ delay: 0.2, duration: 0.7, ease: EASE }}
                  className="font-display text-4xl font-bold tracking-tight text-white sm:text-5xl"
                >
                  Verify your identity
                </motion.h1>
              </div>
              <motion.p
                variants={item}
                custom={2}
                data-testid="onboarding-verification-subtext"
                className="font-sans text-sm leading-relaxed text-white/50"
              >
                Required for account security and compliance — this stays private.
              </motion.p>
            </div>

            <div className="flex flex-col gap-4">
              <motion.div variants={item} custom={3}>
                <AuthField
                  label="ID Number"
                  index="01"
                  testId="verification-id-input"
                  type="text"
                  inputMode="text"
                  autoComplete="off"
                  spellCheck={false}
                  placeholder="e.g. 9001015009087"
                  value={idFocused ? idNumber : maskId(idNumber)}
                  error={errors.idNumber}
                  onFocus={() => setIdFocused(true)}
                  onBlur={() => setIdFocused(false)}
                  onChange={(e) => { setIdNumber(e.target.value); setErrors((p) => ({ ...p, idNumber: undefined })); }}
                  className="font-mono tracking-[0.12em]"
                />
              </motion.div>
              <motion.div variants={item} custom={4}>
                <TrustStrip />
              </motion.div>
              <motion.div variants={item} custom={5}>
                <AuthField
                  label="Residential Address"
                  index="02"
                  testId="verification-address-input"
                  type="text"
                  autoComplete="street-address"
                  placeholder="12 Loop Street, Cape Town, 8001"
                  value={address}
                  error={errors.address}
                  onChange={(e) => { setAddress(e.target.value); setErrors((p) => ({ ...p, address: undefined })); }}
                />
              </motion.div>
            </div>

            <motion.div variants={item} custom={6}>
              <motion.button
                type="submit"
                data-testid="verification-finish-button"
                disabled={submitting}
                whileHover={{ scale: submitting ? 1 : 1.015 }}
                whileTap={{ scale: submitting ? 1 : 0.98 }}
                transition={{ type: "spring", stiffness: 400, damping: 24 }}
                className="group relative flex h-14 w-full items-center justify-center overflow-hidden rounded-2xl bg-safety font-display text-base font-semibold text-white shadow-[0_12px_40px_-12px_rgba(255,91,0,0.7)] disabled:cursor-wait"
              >
                <span
                  aria-hidden
                  className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/25 to-transparent transition-transform duration-700 ease-out group-hover:translate-x-full"
                />
                <AnimatePresence mode="wait" initial={false}>
                  {submitting ? (
                    <motion.span
                      key="loading"
                      initial={{ opacity: 0, y: 6 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -6 }}
                      className="flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em]"
                    >
                      <Loader2 size={16} className="animate-spin" />
                      Encrypting
                    </motion.span>
                  ) : (
                    <motion.span
                      key="idle"
                      initial={{ opacity: 0, y: 6 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -6 }}
                      className="flex items-center gap-2"
                    >
                      Finish Setup
                      <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-1" />
                    </motion.span>
                  )}
                </AnimatePresence>
              </motion.button>
            </motion.div>
          </motion.form>
        )}
      </AnimatePresence>
    </div>
  );
}
